/**
 * buildEventSpans — pure span construction for the canonical event
 * renderer. Builds one `Span` per event after time-truncation,
 * wiring up a `bindContent` callback (CalendarColumnCellContent)
 * and an optional `EventGestureItem` for pointer routing.
 *
 * Lifted out of `CalendarListAliasPlugin.makeSpans` so the aggregator
 * plugin no longer owns event-specific rendering knowledge — the
 * event renderer module does. The aggregator delegates here per
 * subcolumn for now; a future step makes the aggregator type-
 * agnostic by dispatching to per-shape renderers via spec.accepts.
 *
 * Per permissive-renderer-architecture migration plan §Phase D.
 */

import type { DateFragment, TimezoneRegion } from "@tento-chrono";
import { TimePoint } from "@tento-chrono";
import { CalendarColumnCellContent } from "@tento-ui/components/calendar/calendar-column-cell-content";
import type { EventItemTimed, Option } from "@tento-lona/sheets";
import { EventItemPresentation, type SheetRow, type TimeFormat } from "@tento-lona";
import type { CalendarSubcolumnSpan } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import {
  EventGestureItem,
  type EventItemDelegate,
} from "@tento-lona/sheets-ui/sheet/gestures/event-gesture-item.ts";
import type { EventActiveState } from "./event-active-state.ts";

export interface BuildEventSpansInput {
  /** Source row whose events are being painted. Carried into the
   *  per-event `EventGestureItem` for `Sheet`-aware gesture handling. */
  readonly row: SheetRow.Joined.Handle;
  /** Events that overlap the host column's UTC range, already
   *  filtered by category/visibility. */
  readonly events: readonly EventItemTimed[];
  /** The host column's windowed fragment — used to truncate each
   *  event's time range to the visible window. */
  readonly windowed: DateFragment.Windowed;
  /** Column timezone — currently unused at the span-builder level
   *  (the host's `bindContent` formats times) but threaded through
   *  so future renderer variants can specialize. */
  readonly tz: TimezoneRegion;
  /** User preference: how the time label writes a time. */
  readonly timeFormat: TimeFormat;
  /** Action delegate for gesture commit (move/resize). Null when
   *  the host wants read-only spans (no `EventGestureItem`). */
  readonly baseDelegate: Option<EventItemDelegate>;
  /** Optional visual opacity for read-only context overlays. */
  readonly contentOpacity: Option<string>;
  /** Shared visual state for events with open details or context menus. */
  readonly eventActiveState: EventActiveState;
  /** The client's event categories and drafts (`client.events`): titles and colours. */
  readonly presentation: EventItemPresentation.Scope;
}

function buildEventSpans(input: BuildEventSpansInput): CalendarSubcolumnSpan[] {
  const {
    row,
    events,
    windowed,
    timeFormat,
    baseDelegate,
    contentOpacity,
    eventActiveState,
    presentation,
  } = input;
  void input.tz;
  const spans: CalendarSubcolumnSpan[] = [];
  for (const event of events) {
    const truncatedTime = windowed.truncate(event.time.time);
    if (!truncatedTime) continue;

    // `SpanContentBinder` (tento-ui-js) passes its ambient `Option`.
    const bindContent = (
      existing: HTMLElement | null | undefined,
    ): CalendarColumnCellContent => {
      const $content = existing instanceof CalendarColumnCellContent
        ? existing
        : CalendarColumnCellContent.make();
      $content.bind({
        label: EventItemPresentation.title(event, presentation) || "Untitled",
        time: TimePoint.Range.fromTodRange(truncatedTime),
        timeLabel: event.time.time.toTz(windowed.fragment.tz),
        use24Hour: timeFormat === "24h",
      });
      $content.assignStyles({
        background: EventItemPresentation.backgroundColor(event, presentation),
        height: "100%",
        opacity: contentOpacity ?? "",
      });
      return $content;
    };

    // Gesture handle for this event — pointer events on the wrapper
    // cell route through `asCellDelegate(GestureInfo)` to the column-
    // level `CalendarGesture.Manager`, where the gestures registered
    // by `TimelineColumnEventRendererPlugin.registerGestures` handle
    // drag intent. Cross-column drag is automatic via the manager's
    // `pointerId` routing.
    const item = baseDelegate
      ? new EventGestureItem(
          event,
          baseDelegate,
          row.variant?.sheetReads ?? null,
          presentation,
        )
      : null;

    spans.push({
      id: event.id,
      bindContent,
      start: event.time.time.start,
      end: event.time.time.end,
      pointerEvents: "all",
      item,
      callbacks: null,
      applyToCell: ($cell) => {
        eventActiveState.bind(event.id, $cell);
      },
    });
  }
  return spans;
}

export namespace EventSpans {
  export const build = buildEventSpans;
}
